import Link from 'components/base/Link'
import Tooltip from 'components/base/Tooltip'
import { NavItem } from 'configs/navbar'
import { c, FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useIntl } from 'lib/intl-utils'
import { useSetAtom } from 'jotai'
import { subnavPaneContentsAtom, subnavPaneVerticalOffsetAtom } from 'components/sidebar/lib/atoms'
import { useRef } from 'react'
import { match } from 'ts-pattern'

type Props = {
  nav: NavItem
  active: boolean
  navIconClassName?: string
  external?: boolean
}

const NavLink: FC<Props> = ({ nav, active, className, navIconClassName, external, children }) => {
  const { t, lang } = useIntl()
  const ctx = useUserContext()
  const { user, currentMall, theme } = ctx
  const LinkComponent = match(external)
    .with(true, () => 'a')
    .when(
      () => nav.route === '',
      () => 'div'
    )
    .otherwise(() => Link)
  const setSubnavPaneContents = useSetAtom(subnavPaneContentsAtom)
  const setSubnavPaneVerticalOffset = useSetAtom(subnavPaneVerticalOffsetAtom)
  const navLinkVerticalOffset = useRef(0)
  const relevantNavItems = [
    nav,
    ...nav.items.filter(
      (item) =>
        item.customVisibilityCondition?.(ctx) ||
        (!item.customVisibilityCondition &&
          user.roles.includes(item.role) &&
          (!item.malls || item.malls.includes(currentMall.code)) &&
          (!item.hosts || item.hosts.includes(theme.hostType)))
    ),
  ]

  const activeTimeout = useRef<NodeJS.Timeout | null>(null)

  const linkNavItem = relevantNavItems
    .slice(1)
    .find((item) => item.route && item.route !== '/logout/')
  const linkHref =
    (linkNavItem ?? nav).route + ((linkNavItem ?? nav).addMall ? `${currentMall.code}/` : '')

  return (
    <Tooltip
      content={t(nav.name)}
      side="right"
      className="large-sidebar:hidden"
      disabled={relevantNavItems.length > 2}
    >
      <LinkComponent
        href={linkHref}
        className={c`relative w-full cursor-pointer ${className}`}
        onMouseEnter={() => {
          activeTimeout.current = setTimeout(
            relevantNavItems.length > 1
              ? () => {
                  setSubnavPaneContents(relevantNavItems)
                  setSubnavPaneVerticalOffset(navLinkVerticalOffset.current)
                }
              : () => {
                  setSubnavPaneContents(null)
                  setSubnavPaneVerticalOffset(0)
                },
            150
          )
        }}
        onMouseLeave={() => (activeTimeout.current ? clearTimeout(activeTimeout.current) : {})}
      >
        {active && (
          <div className="bg-primary-600 large-sidebar:hidden h-1/2 top-3 absolute left-0 w-[3px] rounded-r" />
        )}
        <div
          ref={(el) => {
            if (el) {
              const rect = el.getBoundingClientRect()
              navLinkVerticalOffset.current = rect.top
            }
          }}
          className={c`group large-sidebar:w-full large-sidebar:hover:bg-gray-50 large-sidebar:rounded-md large-sidebar:py-2 large-sidebar:px-3 large-sidebar:justify-start flex flex-col gap-y-2.5 large-sidebar:flex-row items-center justify-center w-full py-3 ${active} large-sidebar:bg-gray-100 large-sidebar:hover:bg-gray-100`}
        >
          {nav.customIcon ? (
            nav.customIcon(ctx, t, nav, active)
          ) : (
            <div className={c`rounded-md p-1.5 -m-1.5 flex items-center justify-center`}>
              <nav.icon
                className={c`${navIconClassName} group-hover:text-gray-900 large-sidebar:text-gray-400 large-sidebar:group-hover:text-gray-500 group-hover:duration-100 large-sidebar:-ml-1 large-sidebar:mr-3 shrink-0 w-6 h-6 large-sidebar:w-6 large-sidebar:h-6 transition-colors duration-200 ${active} text-gray-800 large-sidebar:text-gray-500 | text-gray-500`}
              />
            </div>
          )}
          <span
            className={c`large-sidebar:block group-hover:text-gray-900 group-hover:duration-100 hidden font-medium text-gray-600 transition-colors duration-200 ${active} large-sidebar:text-gray-900`}
          >
            {nav.customName ? nav.customName(ctx, t, nav, active) : t(nav.name)}
          </span>
          {/* <span
            className={c`large-sidebar:hidden [font-variation-settings:'wdth'_25] leading-[14px] text-[10px] text-center group-hover:text-gray-900 group-hover:duration-100 font-medium text-gray-600 transition-colors duration-200 ${active} text-primary-800 large-sidebar:text-gray-900`}
          >
            {t(nav.name)}
          </span> */}
          {children}
        </div>
      </LinkComponent>
    </Tooltip>
  )
}

export default NavLink
